import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdAssessment, MdEdit, MdFileDownload, MdPrint, MdSave } from 'react-icons/md';
import PageHeader from '../../components/owner/PageHeader';
import { usePanelToast } from '../../context/PanelToastContext';
import { PAYMENTS_UPDATE_EVENT } from '../receptionist/paymentStore';
import {
  fetchPaymentsForDate,
  fetchMembershipSalesForDate,
  fetchZReportActiveMemberStats,
} from '../../services/receptionist/receptionistApi';
import {
  fetchExpensesForDate,
  fetchDailyZReport,
  saveDailyZReport,
  fetchCoaZReportExpenses,
  fetchCoaPettyCashLedgerPayments,
} from '../../services/admin/financeLedgerApi';
import { getCachedGymId, ensureGymId } from '../../services/receptionist/gymContext';
import { isReceptionistPanelScope } from '../../services/gym/branchContext';
import { pkTodayKey } from '../../utils/pkTime';
import { loadAdminSettings } from '../admin/settings/settingsData';
import {
  buildDailyZSheetAuto,
  calcZClosingCash,
  localDateKey,
  loadManualExpenses,
  loadPettyCashCoaExpenses,
  mapApiExpenseToManual,
  mergeManualExpenses,
  expenseCalendarDay,
} from '../admin/finance/financeData';
import {
  printDailyZReport,
  exportDailyZReportCsv,
  exportDailyZReportExcel,
  exportDailyZReportPdf,
} from './DailyZReportExport';
import { useZReportRanges, clearZReportRangeCache } from './zReportTableRange';
import {
  Z1_BANK_ACCOUNTS,
  emptyZBankAccounts,
  mergeZBankAccounts,
  computeZBankClosings,
} from './zReport1Data';
import '../admin/finance/finance.css';
import './DailyZReport.css';

function money(value) {
  const n = Number(value) || 0;
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

/** First letter of each word capital, rest small — e.g. Bank Summery → Bank Summery */
function titleCaseLabel(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/(^|[\s/+_-])([a-z])/g, (_, sep, ch) => `${sep}${ch.toUpperCase()}`);
}

function formatReportDate(isoDay) {
  const day = String(isoDay || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return day || '—';
  const d = new Date(`${day}T12:00:00`);
  if (Number.isNaN(d.getTime())) return day;
  return `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}`;
}

/** Calendar day before YYYY-MM-DD (local noon to avoid DST skew). */
function previousReportDate(isoDay) {
  const day = String(isoDay || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return '';
  const d = new Date(`${day}T12:00:00`);
  d.setDate(d.getDate() - 1);
  return localDateKey(d);
}

function isBlankAmount(value) {
  return String(value ?? '').trim() === '';
}

function emptyManual() {
  return {
    openingCash: '',
    cashReceived: '',
    bankAccounts: emptyZBankAccounts(),
    // Legacy single-column fields kept for export/backward compat
    openingBank: '',
    bankReceived: '',
    bankSale: '',
    pearlGroupCash: '',
    pearlGroupBank: '',
    totalSaleSummary: {
      new_membership: '',
      renewal: '',
      pt_sale: '',
      guest_pass: '',
      locker_sale: '',
      spa_sale: '',
      oxybar: '',
      zumba_sale: '',
      yoga_sale: '',
      gym_apparel: '',
      miscellaneous_sale: '',
      remaining_clear: '',
    },
    totalExpenseSummary: {
      gym_cash: '',
      gym_bank: '',
      pearl_cash: '',
      pearl_bank: '',
    },
    totalSaleMethods: {
      cash: '',
      card: '',
      bank: '',
      qr: '',
      remaining_balance: '',
    },
    totalActiveMembers: {
      membership_sale_month: '',
      pt_sale_month: '',
      active_men: '',
      active_women: '',
    },
  };
}

function stringifyBlock(blankBlock, remoteBlock = {}) {
  const out = { ...blankBlock };
  Object.keys(out).forEach((key) => {
    const value = remoteBlock?.[key];
    out[key] = value === 0 || value ? String(value) : '';
  });
  return out;
}

/** Map saved Supabase Z-report row into editable form state. */
function remoteToManual(remote) {
  const blank = emptyManual();
  if (!remote || !remote.id) return blank;
  const expense = stringifyBlock(blank.totalExpenseSummary, remote.totalExpenseSummary);
  // Never copy summary pearl into expense pearl (or the reverse)
  return {
    openingCash: remote.openingCash ? String(remote.openingCash) : '',
    cashReceived: remote.cashReceived ? String(remote.cashReceived) : '',
    bankAccounts: mergeZBankAccounts(remote.bankAccounts, {
      openingBank: remote.openingBank,
      bankReceived: remote.bankReceived,
      bankSale: remote.bankSale,
      pearlGroupBank: remote.pearlGroupBank,
    }),
    openingBank: remote.openingBank ? String(remote.openingBank) : '',
    bankReceived: remote.bankReceived ? String(remote.bankReceived) : '',
    bankSale: remote.bankSale != null && remote.bankSale !== '' ? String(remote.bankSale) : '',
    pearlGroupCash: Number(remote.pearlGroupCash) > 0 ? String(remote.pearlGroupCash) : '',
    pearlGroupBank: Number(remote.pearlGroupBank) > 0 ? String(remote.pearlGroupBank) : '',
    totalSaleSummary: stringifyBlock(blank.totalSaleSummary, remote.totalSaleSummary),
    totalExpenseSummary: {
      gym_cash: expense.gym_cash || '',
      gym_bank: expense.gym_bank || '',
      pearl_cash: expense.pearl_cash || '',
      pearl_bank: expense.pearl_bank || '',
    },
    totalSaleMethods: stringifyBlock(blank.totalSaleMethods, remote.totalSaleMethods),
    totalActiveMembers: stringifyBlock(blank.totalActiveMembers, remote.totalActiveMembers),
  };
}

function cloneManual(manual) {
  return {
    ...manual,
    bankAccounts: Object.fromEntries(
      Z1_BANK_ACCOUNTS.map((acc) => [
        acc,
        { ...manual.bankAccounts?.[acc] },
      ]),
    ),
    totalSaleSummary: { ...manual.totalSaleSummary },
    totalExpenseSummary: { ...manual.totalExpenseSummary },
    totalSaleMethods: { ...manual.totalSaleMethods },
    totalActiveMembers: { ...manual.totalActiveMembers },
  };
}

function ManualInput({ value, onChange, ariaLabel, readOnly = false }) {
  if (readOnly) {
    return (
      <span className="dz-manual-value" aria-label={ariaLabel}>
        {value === '' || value == null ? '0' : money(value)}
      </span>
    );
  }
  return (
    <input
      type="number"
      min="0"
      step="1"
      className="dz-manual-input"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={ariaLabel}
      placeholder="0"
    />
  );
}

/**
 * Spreadsheet-style Daily Z-Report used by:
 * - Receptionist → Z Report
 * - Admin Finance → Z Report tab
 */
export default function DailyZReport({
  title = 'Z Report',
  subtitle = 'Cash/Bank & sale tables follow the report date; Total Sale / Method / Expense have their own date ranges',
  embedded = false,
  initialDate = '',
} = {}) {
  const toast = usePanelToast();
  const receptionistScope = isReceptionistPanelScope();
  const todayPk = pkTodayKey();
  const [reportDate, setReportDate] = useState(() => (
    isReceptionistPanelScope()
      ? pkTodayKey()
      : (initialDate || localDateKey())
  ));
  const [gymId, setGymId] = useState(() => getCachedGymId() || '');
  const [payments, setPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [activeMemberStats, setActiveMemberStats] = useState({
    membership_sale_month: 0,
    pt_sale_month: 0,
    active_men: 0,
    active_women: 0,
  });
  const [manual, setManual] = useState(() => emptyManual());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(true);
  const [hasSavedReport, setHasSavedReport] = useState(false);
  const draftRef = useRef(null);
  const isEditingRef = useRef(true);
  /** Bumps when a newer load/save starts so stale fetches cannot overwrite form state. */
  const loadSeqRef = useRef(0);
  /** Tracks last applied keys for editable sale / method / expense blocks. */
  const totalSaleAppliedRef = useRef('');
  const totalMethodAppliedRef = useRef('');
  const expenseAppliedRef = useRef('');

  const {
    totalSaleRange,
    expenseRange,
    totalMethodRange,
    reportDayAuto,
    reportDayRefreshing,
    totalsLoading,
    expensesLoading,
  } = useZReportRanges(reportDate, gymId);

  useEffect(() => {
    if (receptionistScope && reportDate !== todayPk) {
      setReportDate(todayPk);
    }
  }, [receptionistScope, reportDate, todayPk]);

  useEffect(() => {
    isEditingRef.current = isEditing;
  }, [isEditing]);

  const gymTitle = useMemo(() => {
    const settings = loadAdminSettings(gymId);
    const name = settings?.gym?.name?.trim() || settings?.softwareName?.trim() || 'OXYGYM HEALTH & FITNESS CLUB';
    const branch = settings?.gym?.branch?.trim() || settings?.gym?.address?.trim() || '';
    return { name: name.toUpperCase(), branch: branch.toUpperCase() };
  }, [gymId]);

  const loadData = useCallback(async ({ preserveEdit = false } = {}) => {
    const seq = ++loadSeqRef.current;
    try {
      clearZReportRangeCache();
      setError('');
      if (!preserveEdit) setLoading(true);
      const gid = getCachedGymId() || await ensureGymId().catch(() => '');
      if (gid) setGymId(gid);

      // Critical path only — guest passes come from payments; member stats load in background
      const [paymentRows, membershipSales, expenseRows, coaExpenseRows, coaPettyRows, remote] = await Promise.all([
        fetchPaymentsForDate(reportDate).catch(() => []),
        fetchMembershipSalesForDate(reportDate).catch(() => []),
        fetchExpensesForDate(reportDate, gid || undefined).catch(() => []),
        fetchCoaZReportExpenses(reportDate, gid || undefined).catch(() => []),
        fetchCoaPettyCashLedgerPayments(reportDate, gid || undefined).catch(() => []),
        fetchDailyZReport(reportDate, gid).catch((err) => {
          if (/z_reports/i.test(err.message || '')) setError(err.message);
          return null;
        }),
      ]);
      if (seq !== loadSeqRef.current) return;

      // New Memberships: memberships.start_date rows + subscription payments collected
      // this day whose membership starts on another day (so cash is not lost).
      const isSubscriptionSource = (p) => {
        const src = String(p?.source || '').toLowerCase().replace(/-/g, '_');
        return src === 'subscription'
          || src === 'membership'
          || src === 'new_membership'
          || src === 'subscriptions';
      };
      const isRenewalMembershipRow = (row) => {
        const src = String(row?.source || '').toLowerCase().replace(/-/g, '_');
        if (src === 'renewal_sale' || src === 'renewal') return true;
        if (/\brenewal\b/i.test(String(row?.package || row?.package_name || ''))) return true;
        const note = String(row?.paymentNote || row?.payment_note || '');
        return /(^|[·|]\s*)(Renewed|Complimentary renewal)\b/i.test(note);
      };
      const nonNewMembershipPayments = (paymentRows || []).filter((p) => !isSubscriptionSource(p));
      const newMembershipRows = (membershipSales || []).filter((row) => !isRenewalMembershipRow(row));
      const coveredPaymentIds = new Set(
        newMembershipRows.map((r) => String(r.rawId || r.id || '')).filter((id) => id && id !== '—' && !String(id).startsWith('mem-')),
      );
      const membershipMemberIds = new Set(
        newMembershipRows
          .map((r) => String(r.memberId || r.member_id || ''))
          .filter(Boolean),
      );
      const orphanSubscriptionPayments = (paymentRows || []).filter((p) => {
        if (!isSubscriptionSource(p)) return false;
        const pid = String(p.rawId || p.id || '');
        // Only skip the exact payment already attached to a membership-day row.
        if (pid && coveredPaymentIds.has(pid)) return false;
        // Same member already on today's start_date membership row — avoid double-count
        const mid = String(p.memberId || p.member_id || '');
        if (mid && membershipMemberIds.has(mid)) return false;
        return true;
      });
      const mergedPayments = [
        ...nonNewMembershipPayments,
        ...newMembershipRows,
        ...orphanSubscriptionPayments,
      ];

      // Expenses for Z: same calendar day only (never createdAt). Petty CoA kept for detail.
      const onReportDay = (row) => expenseCalendarDay(row) === reportDate;
      const apiExpenses = (expenseRows || [])
        .map(mapApiExpenseToManual)
        .filter(onReportDay);
      const coaMapped = (coaExpenseRows || [])
        .map((row) => mapApiExpenseToManual(row))
        .filter(onReportDay);
      const coaPettyMapped = (coaPettyRows || [])
        .map((row) => mapApiExpenseToManual(row))
        .filter(onReportDay);
      const localCoa = (loadPettyCashCoaExpenses(gid, reportDate) || []).filter(onReportDay);
      const dayManualExpenses = loadManualExpenses(gid).filter(onReportDay);
      const allExpenses = mergeManualExpenses(
        mergeManualExpenses(
          mergeManualExpenses([...apiExpenses, ...coaMapped], coaPettyMapped),
          localCoa,
        ),
        dayManualExpenses,
      );
      if (seq !== loadSeqRef.current) return;

      setPayments(mergedPayments);
      setExpenses(allExpenses);

      const saved = Boolean(remote?.id);
      setHasSavedReport(saved);

      if (!preserveEdit) {
        let nextManual = saved ? remoteToManual(remote) : emptyManual();

        // Opening Cash/Bank ← previous day's saved Closing (only when blank)
        const needCashOpen = isBlankAmount(nextManual.openingCash);
        const needBankOpen = Z1_BANK_ACCOUNTS.some(
          (acc) => isBlankAmount(nextManual.bankAccounts?.[acc]?.opening),
        );
        if ((needCashOpen || needBankOpen) && gid) {
          const prevDay = previousReportDate(reportDate);
          if (prevDay) {
            const prevRemote = await fetchDailyZReport(prevDay, gid).catch(() => null);
            if (prevRemote?.id) {
              if (needCashOpen) {
                nextManual = {
                  ...nextManual,
                  openingCash: String(Math.round(Number(prevRemote.closingCash) || 0)),
                };
              }
              if (needBankOpen) {
                const bankAccounts = { ...nextManual.bankAccounts };
                Z1_BANK_ACCOUNTS.forEach((acc) => {
                  if (!isBlankAmount(bankAccounts[acc]?.opening)) return;
                  const closing = prevRemote.closingBanks?.[acc]
                    ?? prevRemote.bankAccounts?.[acc]?.closing;
                  bankAccounts[acc] = {
                    ...bankAccounts[acc],
                    opening: String(Math.round(Number(closing) || 0)),
                  };
                });
                nextManual = { ...nextManual, bankAccounts };
              }
            }
          }
        }

        setManual(nextManual);
        draftRef.current = cloneManual(nextManual);
        // Saved report → view mode; new date → edit mode
        setIsEditing(!saved);
        totalSaleAppliedRef.current = '';
        totalMethodAppliedRef.current = '';
        expenseAppliedRef.current = '';
      } else if (!isEditingRef.current) {
        // Live sales refresh while viewing: keep saved manual values in sync
        const nextManual = saved ? remoteToManual(remote) : emptyManual();
        setManual(nextManual);
        draftRef.current = cloneManual(nextManual);
      }
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      setError(err.message || 'Failed to load Z Report');
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }

    // Background — does not block the sheet
    fetchZReportActiveMemberStats(reportDate)
      .then((stats) => {
        if (seq !== loadSeqRef.current) return;
        setActiveMemberStats(stats || {
          membership_sale_month: 0,
          pt_sale_month: 0,
          active_men: 0,
          active_women: 0,
        });
      })
      .catch(() => {});
  }, [reportDate]);

  useEffect(() => {
    loadData({ preserveEdit: false });
  }, [loadData]);

  useEffect(() => {
    const refresh = () => {
      clearZReportRangeCache();
      loadData({ preserveEdit: true });
    };
    window.addEventListener(PAYMENTS_UPDATE_EVENT, refresh);
    return () => window.removeEventListener(PAYMENTS_UPDATE_EVENT, refresh);
  }, [loadData]);

  // Day-scoped auto — expenses/loans from JS; sale/payment from RPC when available
  const jsAuto = useMemo(
    () => buildDailyZSheetAuto({
      date: reportDate,
      payments,
      guestPassIncome: [],
      expenses,
      gymId,
    }),
    [reportDate, payments, expenses, gymId],
  );

  const auto = useMemo(() => {
    if (!reportDayAuto?.fromRpc) return jsAuto;
    return {
      ...jsAuto,
      saleSummary: reportDayAuto.saleSummary,
      totalSaleCount: reportDayAuto.totalSaleCount,
      totalSaleValue: reportDayAuto.totalSaleValue,
      paymentMethods: reportDayAuto.paymentMethods,
      totalReceivedCount: reportDayAuto.totalReceivedCount,
      totalReceivedValue: reportDayAuto.totalReceivedValue,
      cashSale: reportDayAuto.cashSale,
      bankSaleAuto: reportDayAuto.bankSaleAuto,
    };
  }, [jsAuto, reportDayAuto]);

  // Total Expense Summary — follows that table's own date range
  useEffect(() => {
    if (loading || expensesLoading || expenseRange.refreshing) return;
    const rangeKey = `${expenseRange.dataStart}|${expenseRange.dataEnd}`;
    const rangeChanged = expenseAppliedRef.current !== ''
      && expenseAppliedRef.current !== rangeKey;

    setManual((prev) => {
      let changed = false;
      const next = cloneManual(prev);
      const fillBlank = (block, key, amount) => {
        const cur = String(next[block][key] ?? '').trim();
        const live = amount > 0 ? String(Math.round(amount)) : (hasSavedReport ? '' : '0');
        if (rangeChanged || !hasSavedReport) {
          if (String(next[block][key] ?? '') !== live) {
            next[block][key] = live;
            changed = true;
          }
          return;
        }
        if ((cur === '' || cur === '0') && amount > 0) {
          next[block][key] = String(Math.round(amount));
          changed = true;
        }
      };

      fillBlank('totalExpenseSummary', 'gym_cash', expenseRange.auto.cashExpenses);
      fillBlank('totalExpenseSummary', 'gym_bank', expenseRange.auto.bankExpenses);
      fillBlank('totalExpenseSummary', 'pearl_cash', expenseRange.auto.loanCashExpenses);
      fillBlank('totalExpenseSummary', 'pearl_bank', expenseRange.auto.loanBankExpenses);

      return changed ? next : prev;
    });
    expenseAppliedRef.current = rangeKey;
  }, [
    loading,
    expensesLoading,
    expenseRange.refreshing,
    expenseRange.dataStart,
    expenseRange.dataEnd,
    hasSavedReport,
    expenseRange.auto.cashExpenses,
    expenseRange.auto.bankExpenses,
    expenseRange.auto.loanCashExpenses,
    expenseRange.auto.loanBankExpenses,
  ]);

  // Cash/Bank SUMMARY → PEARL GROUP from day loan vouchers (LTL/STL), blank/new only
  useEffect(() => {
    if (loading) return;
    const loanCash = Math.round(Number(auto.loanCashExpenses) || 0);
    const loanBank = Math.round(Number(auto.loanBankExpenses) || 0);
    setManual((prev) => {
      let changed = false;
      const next = cloneManual(prev);
      const fillPearl = (key, amount) => {
        const cur = String(next[key] ?? '').trim();
        if (hasSavedReport && cur !== '' && cur !== '0') return;
        const live = amount > 0 ? String(amount) : (hasSavedReport ? '' : '0');
        if (!hasSavedReport || cur === '' || cur === '0') {
          if (String(next[key] ?? '') !== live) {
            next[key] = live;
            changed = true;
          }
        }
      };
      fillPearl('pearlGroupCash', loanCash);
      fillPearl('pearlGroupBank', loanBank);
      return changed ? next : prev;
    });
  }, [
    loading,
    hasSavedReport,
    auto.loanCashExpenses,
    auto.loanBankExpenses,
  ]);

  useEffect(() => {
    if (loading) return;
    setManual((prev) => {
      let changed = false;
      const next = cloneManual(prev);
      const fillBlank = (key, amount) => {
        const cur = String(next.totalActiveMembers[key] ?? '').trim();
        if (cur !== '' && cur !== '0') return;
        const n = Math.round(Number(amount) || 0);
        if (n <= 0 && hasSavedReport) return;
        const nextVal = n > 0 ? String(n) : (hasSavedReport ? '' : '0');
        if (String(next.totalActiveMembers[key] ?? '') !== nextVal) {
          next.totalActiveMembers[key] = nextVal;
          changed = true;
        }
      };
      fillBlank('membership_sale_month', activeMemberStats.membership_sale_month);
      fillBlank('pt_sale_month', activeMemberStats.pt_sale_month);
      fillBlank('active_men', activeMemberStats.active_men);
      fillBlank('active_women', activeMemberStats.active_women);
      return changed ? next : prev;
    });
  }, [
    loading,
    hasSavedReport,
    activeMemberStats.membership_sale_month,
    activeMemberStats.pt_sale_month,
    activeMemberStats.active_men,
    activeMemberStats.active_women,
  ]);

  // TOTAL Sale Summary — follows that table's own date range
  useEffect(() => {
    if (loading || totalsLoading || totalSaleRange.refreshing) return;
    const rangeKey = `${totalSaleRange.dataStart}|${totalSaleRange.dataEnd}`;
    const rangeChanged = totalSaleAppliedRef.current !== ''
      && totalSaleAppliedRef.current !== rangeKey;
    const saleAmt = (id) => Number(totalSaleRange.auto.saleSummary?.find((r) => r.id === id)?.value) || 0;
    const saleKeys = [
      'new_membership', 'renewal', 'pt_sale', 'guest_pass', 'locker_sale',
      'spa_sale', 'oxybar', 'zumba_sale', 'yoga_sale', 'gym_apparel',
      'miscellaneous_sale', 'remaining_clear',
    ];

    setManual((prev) => {
      const next = cloneManual(prev);
      let changed = false;
      saleKeys.forEach((key) => {
        const amount = saleAmt(key);
        const live = amount > 0 ? String(Math.round(amount)) : '';
        const cur = String(next.totalSaleSummary[key] ?? '').trim();
        const liveNum = Number(live) || 0;
        const curNum = Number(cur.replace(/,/g, '')) || 0;
        const staleAuto = liveNum > 0 && liveNum !== curNum;
        if (rangeChanged || !hasSavedReport || staleAuto) {
          if (String(next.totalSaleSummary[key] ?? '') !== live) {
            next.totalSaleSummary[key] = live;
            changed = true;
          }
        } else if ((cur === '' || cur === '0') && amount > 0) {
          next.totalSaleSummary[key] = live;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    totalSaleAppliedRef.current = rangeKey;
  }, [
    loading,
    totalsLoading,
    totalSaleRange.refreshing,
    totalSaleRange.dataStart,
    totalSaleRange.dataEnd,
    hasSavedReport,
    totalSaleRange.auto.saleSummary,
  ]);

  // TOTAL Payment Method — follows that table's own date range (BANK SALE stays manual)
  useEffect(() => {
    if (loading || totalsLoading || totalMethodRange.refreshing) return;
    const rangeKey = `${totalMethodRange.dataStart}|${totalMethodRange.dataEnd}`;
    const rangeChanged = totalMethodAppliedRef.current !== ''
      && totalMethodAppliedRef.current !== rangeKey;
    const methodAmt = (id) => Number(totalMethodRange.auto.paymentMethods?.find((r) => r.id === id)?.amount) || 0;
    const methodKeys = ['cash', 'card', 'bank', 'qr'];
    const pendingAmt = Number(totalMethodRange.auto.paymentPending) || 0;
    const pendingLive = pendingAmt > 0 ? String(Math.round(pendingAmt)) : '';

    setManual((prev) => {
      const next = cloneManual(prev);
      let changed = false;
      methodKeys.forEach((key) => {
        const amount = methodAmt(key);
        const live = amount > 0 ? String(Math.round(amount)) : '';
        const cur = String(next.totalSaleMethods[key] ?? '').trim();
        if (rangeChanged || !hasSavedReport) {
          if (String(next.totalSaleMethods[key] ?? '') !== live) {
            next.totalSaleMethods[key] = live;
            changed = true;
          }
        } else if ((cur === '' || cur === '0') && amount > 0) {
          next.totalSaleMethods[key] = live;
          changed = true;
        }
      });
      const pendingCur = String(next.totalSaleMethods.remaining_balance ?? '').trim();
      if (rangeChanged || !hasSavedReport) {
        if (String(next.totalSaleMethods.remaining_balance ?? '') !== pendingLive) {
          next.totalSaleMethods.remaining_balance = pendingLive;
          changed = true;
        }
      } else if ((pendingCur === '' || pendingCur === '0') && pendingAmt > 0) {
        next.totalSaleMethods.remaining_balance = pendingLive;
        changed = true;
      }
      return changed ? next : prev;
    });
    totalMethodAppliedRef.current = rangeKey;
  }, [
    loading,
    totalsLoading,
    totalMethodRange.refreshing,
    totalMethodRange.dataStart,
    totalMethodRange.dataEnd,
    hasSavedReport,
    totalMethodRange.auto.paymentMethods,
    totalMethodRange.auto.paymentPending,
  ]);

  const openingCash = Number(manual.openingCash) || 0;
  const cashReceived = Number(manual.cashReceived) || 0;
  const bankClosings = useMemo(
    () => computeZBankClosings(manual.bankAccounts),
    [manual.bankAccounts],
  );
  const closingBanks = bankClosings.closingBanks;
  const closingBank = bankClosings.closingBank;
  const gymBank = Number(manual.totalExpenseSummary.gym_bank) || 0;
  // Total Expense Summary Pearl (monthly / separate — not linked to cash/bank summary)
  const pearlExpenseCash = Number(manual.totalExpenseSummary.pearl_cash) || 0;
  const pearlExpenseBank = Number(manual.totalExpenseSummary.pearl_bank) || 0;
  // Cash / Bank SUMMARY Pearl (separate fields)
  const pearlGroupCash = Number(manual.pearlGroupCash) || 0;
  const pearlGroupBank = Number(manual.pearlGroupBank) || 0;

  const closingCash = calcZClosingCash({
    openingCash,
    cashReceived,
    cashSale: auto.cashSale,
    cashExpenses: auto.cashExpenses,
    pearlGroupCash,
  });

  const gymCash = Number(manual.totalExpenseSummary.gym_cash) || 0;
  const gymExpenseTotal = gymCash + gymBank;
  const pearlExpenseTotal = pearlExpenseCash + pearlExpenseBank;
  const totalExpenseCash = gymCash + pearlExpenseCash;
  const totalExpenseBank = gymBank + pearlExpenseBank;

  const saleSummaryGrandTotal = Object.values(manual.totalSaleSummary || {}).reduce(
    (sum, v) => sum + (Number(v) || 0),
    0,
  );
  // TOTAL Payment Method — editable; auto-filled from sale date range
  const saleMethodsGrandTotal = (
    (Number(manual.totalSaleMethods.cash) || 0)
    + (Number(manual.totalSaleMethods.card) || 0)
    + (Number(manual.totalSaleMethods.bank) || 0)
    + (Number(manual.totalSaleMethods.qr) || 0)
  );

  const setTop = (key, value) => {
    if (!isEditing) return;
    setManual((prev) => ({ ...prev, [key]: value }));
  };
  const setBlock = (block, key, value) => {
    if (!isEditing) return;
    setManual((prev) => ({
      ...prev,
      [block]: { ...prev[block], [key]: value },
    }));
  };
  const setBank = (account, field, value) => {
    if (!isEditing) return;
    setManual((prev) => ({
      ...prev,
      bankAccounts: {
        ...prev.bankAccounts,
        [account]: { ...prev.bankAccounts[account], [field]: value },
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const gid = gymId || getCachedGymId() || await ensureGymId();
      // Keep Cash/Bank SUMMARY pearl and Total Expense Summary pearl fully separate
      const expenseOnly = {
        gym_cash: manual.totalExpenseSummary?.gym_cash ?? '',
        gym_bank: manual.totalExpenseSummary?.gym_bank ?? '',
        pearl_cash: manual.totalExpenseSummary?.pearl_cash ?? '',
        pearl_bank: manual.totalExpenseSummary?.pearl_bank ?? '',
      };
      // Snapshot form values now — do not rely on a later remote round-trip
      const formSnapshot = cloneManual(manual);

      await saveDailyZReport({
        reportDate,
        openingCash,
        cashReceived,
        bankAccounts: formSnapshot.bankAccounts,
        closingBanks,
        closingCash,
        closingBank,
        pearlGroupCash,
        pearlGroupBank,
        totalSaleSummary: formSnapshot.totalSaleSummary,
        totalExpenseSummary: expenseOnly,
        totalSaleMethods: formSnapshot.totalSaleMethods,
        totalActiveMembers: formSnapshot.totalActiveMembers,
      }, gid);

      // Invalidate any in-flight load that fetched the pre-save row (was reverting BANK SALE)
      loadSeqRef.current += 1;

      const nextManual = cloneManual(formSnapshot);
      setManual(nextManual);
      draftRef.current = cloneManual(nextManual);
      setHasSavedReport(true);
      setIsEditing(false);
      toast.showSaved('Z Report');
    } catch (err) {
      toast.showError(err.message || 'Could not save Z Report', 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = () => {
    draftRef.current = cloneManual(manual);
    setIsEditing(true);
    toast.showSuccess(
      `Editing Z Report for ${formatReportDate(reportDate)}. Change fields, then Save.`,
      'Edit mode',
    );
  };

  const handleCancelEdit = () => {
    if (draftRef.current) {
      setManual(cloneManual(draftRef.current));
    }
    setIsEditing(false);
    toast.showSuccess('Changes discarded. Showing saved Z Report.', 'View mode');
  };

  const buildExportPayload = () => ({
    gymTitle,
    reportDate,
    auto: {
      ...auto,
      loanCashExpenses: expenseRange.auto.loanCashExpenses,
      loanBankExpenses: expenseRange.auto.loanBankExpenses,
    },
    manual: {
      ...manual,
      bankAccounts: manual.bankAccounts,
    },
    closingCash,
    closingBank,
    closingBanks,
    gymExpenseTotal,
    pearlExpenseTotal,
    totalExpenseCash,
    totalExpenseBank,
    saleSummaryGrandTotal,
    saleMethodsGrandTotal,
    pearlGroupCash,
    pearlGroupBank,
  });

  const handlePrint = () => {
    const ok = printDailyZReport(buildExportPayload());
    if (!ok) {
      toast.showError('Allow pop-ups to print the Z Report.', 'Print blocked');
    }
  };

  const handleExportCsv = () => {
    exportDailyZReportCsv(buildExportPayload());
    toast.showSuccess('CSV download started.', 'Exported');
  };

  const handleExportExcel = () => {
    exportDailyZReportExcel(buildExportPayload());
    toast.showSuccess('Excel download started.', 'Exported');
  };

  const handleExportPdf = () => {
    try {
      const ok = exportDailyZReportPdf(buildExportPayload());
      if (ok === false) {
        toast.showError('Could not create PDF file.', 'Export failed');
        return;
      }
      toast.showSuccess('PDF download started.', 'Exported');
    } catch (err) {
      toast.showError(err.message || 'Could not export PDF', 'Export failed');
    }
  };

  const toolbarActions = (
    <div className="report-toolbar-actions dz-toolbar-actions dz-toolbar-row">
      <div className="dz-toolbar-date">
        <label className="owner-form-label" htmlFor={embedded ? 'dz-date-embedded' : 'dz-date'}>Date</label>
        <input
          id={embedded ? 'dz-date-embedded' : 'dz-date'}
          type="date"
          className="owner-form-input"
          value={receptionistScope ? todayPk : reportDate}
          max={receptionistScope ? todayPk : undefined}
          readOnly={receptionistScope}
          disabled={receptionistScope}
          onChange={(e) => {
            if (receptionistScope) return;
            setReportDate(e.target.value || localDateKey());
          }}
        />
      </div>
      <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={handlePrint}>
        <MdPrint aria-hidden /> Print
      </button>
      <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={handleExportCsv}>
        <MdFileDownload aria-hidden /> Export CSV
      </button>
      <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={handleExportExcel}>
        <MdFileDownload aria-hidden /> Export Excel
      </button>
      <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={handleExportPdf}>
        <MdFileDownload aria-hidden /> Export PDF
      </button>
      {isEditing && hasSavedReport ? (
        <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={handleCancelEdit}>
          Cancel
        </button>
      ) : null}
      {isEditing ? (
        <button type="button" className="owner-btn owner-btn-primary owner-btn-sm dz-toolbar-save" onClick={handleSave} disabled={saving}>
          <MdSave aria-hidden /> {saving ? 'Saving…' : (hasSavedReport ? 'Update Z Report' : 'Save Z Report')}
        </button>
      ) : (
        <button type="button" className="owner-btn owner-btn-primary owner-btn-sm dz-toolbar-save" onClick={handleEdit}>
          <MdEdit aria-hidden /> Edit
        </button>
      )}
    </div>
  );

  const statusBadge = hasSavedReport && !isEditing
    ? <span className="owner-badge owner-badge-info">Saved — view mode</span>
    : (isEditing && hasSavedReport
      ? <span className="owner-badge owner-badge-warning">Editing</span>
      : (!hasSavedReport && isEditing
        ? <span className="owner-badge owner-badge-info">New report</span>
        : null));

  /** Per-table Start/End — each table passes its own range hook result */
  const renderSaleRangeControls = (range, colSpan = 2) => {
    const label = range.start !== range.end
      ? `${formatReportDate(range.start)} → ${formatReportDate(range.end)}`
      : formatReportDate(range.start || reportDate);
    return (
      <>
        <tr className="dz-sale-range-row no-print">
          <td colSpan={colSpan}>
            <div className="dz-sale-range">
              <label className="dz-sale-range-field">
                <span>Start date</span>
                <input
                  type="date"
                  className="owner-form-input"
                  value={receptionistScope ? todayPk : range.start}
                  max={receptionistScope ? todayPk : (range.end || undefined)}
                  readOnly={receptionistScope}
                  disabled={receptionistScope}
                  onChange={(e) => {
                    if (receptionistScope) return;
                    range.setStart(e.target.value || reportDate);
                  }}
                  aria-label="Start date"
                />
              </label>
              <label className="dz-sale-range-field">
                <span>End date</span>
                <input
                  type="date"
                  className="owner-form-input"
                  value={receptionistScope ? todayPk : range.end}
                  min={receptionistScope ? undefined : (range.start || undefined)}
                  max={receptionistScope ? todayPk : undefined}
                  readOnly={receptionistScope}
                  disabled={receptionistScope}
                  onChange={(e) => {
                    if (receptionistScope) return;
                    range.setEnd(e.target.value || reportDate);
                  }}
                  aria-label="End date"
                />
              </label>
              {range.loading && !range.refreshing ? (
                <span className="dz-sale-range-status">Loading…</span>
              ) : null}
              {range.refreshing ? (
                <span className="dz-sale-range-status dz-sale-range-status-muted">Updating…</span>
              ) : null}
            </div>
          </td>
        </tr>
        <tr className="dz-sale-range-print-only">
          <td colSpan={colSpan} className="dz-sale-range-label">
            {label}
          </td>
        </tr>
      </>
    );
  };

  const sheet = (
    <div className={`owner-card rec-panel-card admin-finance-section dz-sheet${isEditing ? '' : ' is-view-only'}`} id="daily-z-report-print">
      <div className="dz-header admin-z-sale-header">
        <div className="dz-brand admin-z-sale-brand">{gymTitle.name}</div>
        {gymTitle.branch ? <div className="dz-branch">{gymTitle.branch}</div> : null}
        <div className="dz-title-row admin-z-sale-subtitle-row">
          <span className="dz-title admin-z-sale-subtitle">Z REPORT</span>
          <span className="dz-date admin-z-sale-date">{formatReportDate(reportDate)}</span>
        </div>
        <p className="dz-showing-date no-print">
          Showing Z Report for <strong>{formatReportDate(reportDate)}</strong>
          {hasSavedReport ? ' (saved)' : ' (not saved yet)'}
          {isEditing ? ' · editable' : ' · view only'}
        </p>
      </div>

      <div className="dz-table-wrap">
        <table className="dz-table dz-bank-cash-table">
          <thead>
            <tr>
              <th colSpan={4}>Bank Summery</th>
              <th colSpan={2}>Cash Summary</th>
            </tr>
            <tr>
              <th>Item</th>
              {Z1_BANK_ACCOUNTS.map((acc) => <th key={acc}>{acc}</th>)}
              <th>Item</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Opening Balance</td>
              {Z1_BANK_ACCOUNTS.map((acc) => (
                <td key={acc} className="dz-num">
                  <ManualInput
                    readOnly={!isEditing}
                    value={manual.bankAccounts[acc].opening}
                    onChange={(v) => setBank(acc, 'opening', v)}
                    ariaLabel={`${acc} opening`}
                  />
                </td>
              ))}
              <td>Opening Balance</td>
              <td className="dz-num">
                <ManualInput readOnly={!isEditing} value={manual.openingCash} onChange={(v) => setTop('openingCash', v)} ariaLabel="Opening cash" />
              </td>
            </tr>
            <tr>
              <td>Bank Received</td>
              {Z1_BANK_ACCOUNTS.map((acc) => (
                <td key={acc} className="dz-num">
                  <ManualInput
                    readOnly={!isEditing}
                    value={manual.bankAccounts[acc].received}
                    onChange={(v) => setBank(acc, 'received', v)}
                    ariaLabel={`${acc} received`}
                  />
                </td>
              ))}
              <td>Cash Received</td>
              <td className="dz-num">
                <ManualInput readOnly={!isEditing} value={manual.cashReceived} onChange={(v) => setTop('cashReceived', v)} ariaLabel="Cash received" />
              </td>
            </tr>
            <tr>
              <td>Bank Sale</td>
              {Z1_BANK_ACCOUNTS.map((acc) => (
                <td key={acc} className="dz-num">
                  <ManualInput
                    readOnly={!isEditing}
                    value={manual.bankAccounts[acc].sale}
                    onChange={(v) => setBank(acc, 'sale', v)}
                    ariaLabel={`${acc} sale`}
                  />
                </td>
              ))}
              <td>Cash Sale</td>
              <td className="dz-num"><strong>{money(auto.cashSale)}</strong></td>
            </tr>
            <tr>
              <td>Pearl Group Expenses</td>
              {Z1_BANK_ACCOUNTS.map((acc) => (
                <td key={acc} className="dz-num">
                  <ManualInput
                    readOnly={!isEditing}
                    value={manual.bankAccounts[acc].pearlGroupExpenses}
                    onChange={(v) => setBank(acc, 'pearlGroupExpenses', v)}
                    ariaLabel={`${acc} pearl expenses`}
                  />
                </td>
              ))}
              <td>Pearl Group Expenses</td>
              <td className="dz-num">
                <ManualInput
                  readOnly={!isEditing}
                  value={manual.pearlGroupCash}
                  onChange={(v) => setTop('pearlGroupCash', v)}
                  ariaLabel="Pearl Group Expenses"
                />
              </td>
            </tr>
            <tr>
              <td>Gym Expenses</td>
              {Z1_BANK_ACCOUNTS.map((acc) => (
                <td key={acc} className="dz-num">
                  <ManualInput
                    readOnly={!isEditing}
                    value={manual.bankAccounts[acc].gymExpenses}
                    onChange={(v) => setBank(acc, 'gymExpenses', v)}
                    ariaLabel={`${acc} gym expenses`}
                  />
                </td>
              ))}
              <td>Gym Expenses</td>
              <td className="dz-num"><strong>{money(auto.cashExpenses)}</strong></td>
            </tr>
            <tr className="dz-total">
              <td>Closing Balance</td>
              {Z1_BANK_ACCOUNTS.map((acc) => (
                <td key={acc} className="dz-num"><strong>{money(closingBanks[acc])}</strong></td>
              ))}
              <td>Closing Balance</td>
              <td className="dz-num"><strong>{money(closingCash)}</strong></td>
            </tr>
          </tbody>
        </table>

        <table className="dz-table dz-paired-table">
          <thead>
            <tr>
              <th colSpan={3}>Sale Summary</th>
              <th colSpan={2}>Total Sale Summary</th>
            </tr>
            <tr>
              <th>Item</th>
              <th>No</th>
              <th>Value</th>
              <th>Item</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {renderSaleRangeControls(totalSaleRange, 5)}
            {[
              ['new_membership', 'New Membership'],
              ['renewal', 'Renewal Membership'],
              ['pt_sale', 'Pt Sale'],
              ['guest_pass', 'Guest Pass'],
              ['locker_sale', 'Locker Sale'],
              ['spa_sale', 'Spa Sale'],
              ['oxybar', 'Oxybar Sale'],
              ['zumba_sale', 'Zumba Sale'],
              ['yoga_sale', 'Yoga Sale'],
              ['gym_apparel', 'Gym Apparel'],
              ['miscellaneous_sale', 'Miscellaneous Sale'],
              ['remaining_clear', 'Remaining Clear'],
            ].map(([key, label]) => {
              const autoRow = (auto.saleSummary || []).find((r) => r.id === key);
              return (
                <tr key={key}>
                  <td>
                    {label}
                    {autoRow?.note ? <div className="dz-note">{titleCaseLabel(autoRow.note)}</div> : null}
                  </td>
                  <td className="dz-num">{autoRow?.count || '—'}</td>
                  <td className="dz-num">{autoRow?.value ? money(autoRow.value) : '0'}</td>
                  <td>{label}</td>
                  <td className="dz-num">
                    <ManualInput
                      readOnly={!isEditing}
                      value={manual.totalSaleSummary[key]}
                      onChange={(v) => setBlock('totalSaleSummary', key, v)}
                      ariaLabel={label}
                    />
                  </td>
                </tr>
              );
            })}
            <tr className="dz-total">
              <td>Total Sale</td>
              <td className="dz-num"><strong>{auto.totalSaleCount || '—'}</strong></td>
              <td className="dz-num"><strong>{money(auto.totalSaleValue)}</strong></td>
              <td>Grand Total</td>
              <td className="dz-num"><strong>{money(saleSummaryGrandTotal)}</strong></td>
            </tr>
          </tbody>
        </table>

        <table className="dz-table dz-paired-table">
          <thead>
            <tr>
              <th colSpan={3}>
                Payment Method
                {reportDayRefreshing ? (
                  <span className="dz-sale-range-status dz-sale-range-status-muted"> Updating…</span>
                ) : null}
              </th>
              <th colSpan={2}>Total Payment Method</th>
            </tr>
            <tr>
              <th>Item</th>
              <th>No</th>
              <th>Sum</th>
              <th>Item</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {renderSaleRangeControls(totalMethodRange, 5)}
            {[
              ['cash', 'Cash'],
              ['card', 'Credit Card'],
              ['bank', 'Bank Transfer'],
              ['qr', 'Qr'],
            ].map(([key, label]) => {
              const autoRow = (auto.paymentMethods || []).find((r) => r.id === key);
              return (
                <tr key={key}>
                  <td>{label}</td>
                  <td className="dz-num">{autoRow?.count || '—'}</td>
                  <td className="dz-num">{autoRow?.amount ? money(autoRow.amount) : '0'}</td>
                  <td>{label}</td>
                  <td className="dz-num">
                    <ManualInput
                      readOnly={!isEditing}
                      value={manual.totalSaleMethods[key]}
                      onChange={(v) => setBlock('totalSaleMethods', key, v)}
                      ariaLabel={label}
                    />
                  </td>
                </tr>
              );
            })}
            <tr className="dz-total">
              <td>Total Received</td>
              <td className="dz-num"><strong>{auto.totalReceivedCount || '—'}</strong></td>
              <td className="dz-num"><strong>{money(auto.totalReceivedValue)}</strong></td>
              <td>Grand Total</td>
              <td className="dz-num"><strong>{money(saleMethodsGrandTotal)}</strong></td>
            </tr>
          </tbody>
        </table>

        <div className="dz-grid-2">
          <table className="dz-table">
            <thead>
              <tr><th colSpan={2}>Total Active Member</th></tr>
              <tr><th>Item</th><th>Amount</th></tr>
            </thead>
            <tbody>
              {[
                ['membership_sale_month', 'Membership Sale During Month'],
                ['pt_sale_month', 'Pt Sale This Month'],
                ['active_men', 'Active Member Men'],
                ['active_women', 'Active Member Women'],
              ].map(([key, label]) => (
                <tr key={key}>
                  <td>{label}</td>
                  <td className="dz-num">
                    <ManualInput
                      readOnly={!isEditing}
                      value={manual.totalActiveMembers[key]}
                      onChange={(v) => setBlock('totalActiveMembers', key, v)}
                      ariaLabel={label}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <table className="dz-table">
            <thead>
              <tr><th colSpan={2}>Total Expense Summary</th></tr>
              <tr><th>Item</th><th>Amount</th></tr>
            </thead>
            <tbody>
              {renderSaleRangeControls(expenseRange, 2)}
              <tr>
                <td>Gym Cash</td>
                <td className="dz-num">
                  <ManualInput readOnly={!isEditing} value={manual.totalExpenseSummary.gym_cash} onChange={(v) => setBlock('totalExpenseSummary', 'gym_cash', v)} ariaLabel="Gym cash" />
                </td>
              </tr>
              <tr>
                <td>Gym Bank</td>
                <td className="dz-num">
                  <ManualInput readOnly={!isEditing} value={manual.totalExpenseSummary.gym_bank} onChange={(v) => setBlock('totalExpenseSummary', 'gym_bank', v)} ariaLabel="Gym bank" />
                </td>
              </tr>
              <tr className="dz-subtotal">
                <td>Total</td>
                <td className="dz-num"><strong>{money(gymExpenseTotal)}</strong></td>
              </tr>
              <tr>
                <td>Pearl Group Cash</td>
                <td className="dz-num">
                  <ManualInput readOnly={!isEditing} value={manual.totalExpenseSummary.pearl_cash} onChange={(v) => setBlock('totalExpenseSummary', 'pearl_cash', v)} ariaLabel="Pearl cash" />
                </td>
              </tr>
              <tr>
                <td>Pearl Group Bank</td>
                <td className="dz-num">
                  <ManualInput readOnly={!isEditing} value={manual.totalExpenseSummary.pearl_bank} onChange={(v) => setBlock('totalExpenseSummary', 'pearl_bank', v)} ariaLabel="Pearl bank" />
                </td>
              </tr>
              <tr className="dz-subtotal">
                <td>Total</td>
                <td className="dz-num"><strong>{money(pearlExpenseTotal)}</strong></td>
              </tr>
              <tr className="dz-total">
                <td>Total Cash</td>
                <td className="dz-num"><strong>{money(totalExpenseCash)}</strong></td>
              </tr>
              <tr className="dz-total">
                <td>Total Bank</td>
                <td className="dz-num"><strong>{money(totalExpenseBank)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  if (embedded) {
    return (
      <div className="dz-page">
        <div className="owner-card rec-panel-card admin-finance-section dz-toolbar no-print">
          <div className="dz-toolbar-head">
            <h2 className="owner-card-title">{title}</h2>
            {statusBadge}
          </div>
          {toolbarActions}
          {error ? <p className="owner-placeholder-text" style={{ color: 'var(--owner-danger, #dc2626)' }}>{error}</p> : null}
          {loading ? <p className="owner-placeholder-text">Loading Z Report…</p> : null}
        </div>
        {sheet}
      </div>
    );
  }

  return (
    <div className="dz-page">
      <PageHeader title={title} subtitle={subtitle} variant="banner">
        <span className="owner-badge owner-badge-info" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <MdAssessment aria-hidden />
          Daily close
        </span>
      </PageHeader>

      {error ? (
        <p className="owner-placeholder-text owner-mb-16" style={{ color: 'var(--owner-danger, #dc2626)' }}>
          {error}
        </p>
      ) : null}
      {loading ? <p className="owner-placeholder-text owner-mb-16">Loading Z Report…</p> : null}

      <div className="owner-card rec-panel-card admin-finance-section dz-toolbar no-print">
        <div className="dz-toolbar-head">
          <h2 className="owner-card-title">Report date</h2>
          {statusBadge}
        </div>
        {toolbarActions}
      </div>

      {sheet}
    </div>
  );
}
